import {
  CircularProgress,
  Grid,
  Typography,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  DefaultButton,
  Footer,
  FormValueTypes,
  PasswordField,
  TextField,
  colors,
  useFormHook,
} from 'design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
// Data Layer
import { useNetworkObserver } from 'data-layer-cr/dist/network';
// Methods
import {
  getCurrentTimeTheme,
  getThemeValues,
} from 'src/functions/themeMethods';
// Recaptcha
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import makeValidationSchema from './makeValidationSchema';
// Redux
import { login } from '../../../store/profile/actions';
// Network
import { LOGIN } from '../../../store/profile/requestIdentifiers';

import HoneyPot from './HoneyPot';

type themeTypes = {
  useLightTheme: boolean;
};

export const getThemeColor = (theme: boolean): string =>
  theme ? colors.madison : colors.white;

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      backgroundSize: '100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'bottom',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      padding: 10,
    },
    formContainer: {
      maxWidth: 400,
      flexGrow: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      [theme.breakpoints.up('md')]: {
        maxWidth: 500,
        padding: theme.spacing(3),
      },
    },
    logoImage: {
      marginBottom: theme.spacing(2),
    },
    welcomeText: {
      display: 'inline-flex',
      flexDirection: 'row',
      alignItems: 'baseline',
    },
    welcomeTextWrapper: {
      marginBottom: theme.spacing(4),
    },
    captchaContiner: {
      marginTop: theme.spacing(3),
    },
    loginButtonContainer: {
      display: 'inline-block',
      width: 335,
      marginTop: theme.spacing(3),
    },
    '@global': {
      // Overrides autocomplete default styles
      'input:-webkit-autofill': {
        '-webkit-text-fill-color': ({ useLightTheme }: themeTypes) =>
          getThemeColor(useLightTheme),

        // For Chrome, Edge
        '-webkit-background-clip': 'text',

        // For Firefox Browser
        filter: 'none',
      },
    },
  }),
);

type RequiredParams = {
  authType: string;
};
export const LoginScreen = ({ requestLogin }: PropsFromRedux): JSX.Element => {
  const [recaptchaToken, setRecaptchaToken] = useState<null | string>(null);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const classes = useStyles({
    useLightTheme: getCurrentTimeTheme(new Date()) === 'light',
  });

  const isRecaptchaDisabled = useFeatureIsOn('fwm-recaptcha-dev-disable');

  const history = useNavigate();
  const { authType } = useParams<RequiredParams>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const onVerify = useCallback((token: string) => {
    setRecaptchaToken(token);
  }, []);

  const {
    backgroundImage,
    welcomeTextColor,
    logoIcon,
    morningNightMessage,
    customInputColors,
    sunMoonIcon,
    footerColor,
  } = getThemeValues(isMobile, new Date());

  const loginRequestStatus = useNetworkObserver(LOGIN);

  useEffect(() => {
    if (loginRequestStatus === 'success') {
      history('/');
    }
  }, [loginRequestStatus, history]);

  function submit(data: { [key: string]: FormValueTypes }): void {
    requestLogin(
      data.userName as string,
      data.password as string,
      authType as string,
      recaptchaToken as string,
    );
  }

  const onRefreshRecaptcha = () => {
    setRefreshReCaptcha((r) => !r);
  };

  const { getInputProps, handleSubmit } = useFormHook(
    { userName: '', password: '', alterName: '' },
    submit,
    makeValidationSchema(),
  );

  function handleFormSubmit(event: React.FormEvent): void {
    event.preventDefault();
    onRefreshRecaptcha();
    handleSubmit();
  }

  useEffect(() => {
    if (authType !== 'AGE' && authType !== 'ASE') {
      history('/AGE/login', { replace: true });
    }
  }, [authType, history]);

  return (
    <div
      className={classes.container}
      data-testid="loginBackground"
      style={{ backgroundImage }}
    >
      <div className={classes.content}>
        <form
          data-testid="login-form"
          className={classes.formContainer}
          onSubmit={handleFormSubmit}
        >
          <Grid container spacing={3}>
            <Grid item xs container alignItems="center" justifyContent="center">
              <img
                className={classes.logoImage}
                src={logoIcon}
                width={230}
                alt="Logo"
              />
            </Grid>
            <Grid item xs={12} container>
              <Grid
                item
                xs={12}
                container
                justifyContent="center"
                alignItems="center"
                className={classes.welcomeTextWrapper}
              >
                <div className={classes.welcomeText}>
                  <Typography
                    variant="body1"
                    style={{ color: welcomeTextColor }}
                  >
                    ¡Hola!
                  </Typography>
                  &nbsp;
                  <Typography
                    variant="caption"
                    style={{ color: welcomeTextColor }}
                  >
                    {morningNightMessage}
                  </Typography>
                </div>
                {sunMoonIcon}
              </Grid>
              <HoneyPot getInputProps={getInputProps} potName="alterName" />
              <Grid item xs={12}>
                <TextField
                  inputId="sales-web-input-username"
                  data-testid="userName-field"
                  inputName="userName"
                  inputLabel="Usuario"
                  type="text"
                  defaultValue={getInputProps('userName').value as string}
                  onChange={getInputProps('userName').onChange}
                  inputError={getInputProps('userName').error}
                  inputErrorMessage={getInputProps('userName').errorText}
                  customColor={customInputColors}
                />
              </Grid>
              <Grid item xs={12}>
                <br />
                <PasswordField
                  inputId="sales-web-input-password"
                  data-testid="password-field"
                  inputName="password"
                  inputLabel="Contraseña"
                  defaultValue={getInputProps('password').value as string}
                  onChange={getInputProps('password').onChange}
                  inputError={getInputProps('password').error}
                  inputErrorMessage={getInputProps('password').errorText}
                  customColor={customInputColors}
                />
              </Grid>
            </Grid>

            <Grid
              container
              justifyContent="center"
              className={classes.captchaContiner}
            >
              <GoogleReCaptcha
                data-testid="recaptcha-element"
                onVerify={onVerify}
                action="LOGIN"
                refreshReCaptcha={refreshReCaptcha}
              />
            </Grid>

            <Grid container justifyContent="center">
              <div className={classes.loginButtonContainer}>
                <DefaultButton
                  id="sales-web-button-login-request"
                  type="submit"
                  fullWidth
                  disabled={
                    (!isRecaptchaDisabled && !recaptchaToken) ||
                    loginRequestStatus === 'requesting'
                  }
                >
                  {loginRequestStatus === 'requesting' ? (
                    <CircularProgress size={20} data-testid="login-progress" />
                  ) : (
                    'Ingresar'
                  )}
                </DefaultButton>
              </div>
            </Grid>
          </Grid>
        </form>
        <Footer
          textColor={footerColor}
          rootStyles={{ border: 'none', background: 'transparent' }}
          IsMobile={isMobile}
          country="Costa Rica"
          linkText="Consulte nuestros términos y condiciones"
          linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
        />
      </div>
    </div>
  );
};

const reduxActions = { requestLogin: login };
const connector = connect(null, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(LoginScreen);
